// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, map } from 'rxjs';
import { DdjDocument } from '@interfaces/document.interfaces';
import { RpcApiResponse } from '@interfaces/api.interfaces';

@Injectable({
    providedIn: 'root',
})
export class VendorService {
    constructor(private readonly apiService: ApiService) {}

    routeOneGetJacket(dealerId: number, dealId: number, firstName: string, lastName: string, vin: string) {
        return this.apiService
            .postV2$('api/RouteOne/jacket', {
                dealerId,
                dealId,
                firstName,
                lastName,
                vin,
            })
            .pipe(
                map((response) => {
                    return response;
                })
            );
    }

    routeOneGetDocument(dealerId: number, dealId: number, documentId: number, obscureSignature = false) {
        return this.apiService
            .postV3$<any>('api/RouteOne/eContract', {
                dealerId,
                dealId,
                documentId,
                obscureSignature,
            })
            .pipe(
                map((response) => {
                    return response;
                })
            );
    }

    routeOneApplySignatures(body: object) {
        return this.apiService.postV3$('api/RouteOne/applySignatures', body).pipe(
            map((response) => {
                return response;
            })
        );
    }

    // TODO
    routeOneGetEContractStatus() {
        return this.apiService.postV2$('api/RouteOne/eContract/status', {}).pipe(
            map((response) => {
                if (response?.data) {
                    return response.data;
                } else {
                    return undefined;
                }
            })
        );
    }

    routeOneSendDocuments(dealerId: number, dealId: number, documents: DdjDocument[]) {
        return this.apiService
            .postV2$('api/RouteOne/sendDocuments', {
                dealerId,
                dealId,
                documentsToSend: documents,
            })
            .pipe(
                map((response) => {
                    return response;
                })
            );
    }

    isDocumentForRouteOne(document: DdjDocument): boolean {
        // In the case we pre-maturely have to close the document
        // Ex: invalid signature images
        if (!document) {
            return false;
        }

        // RouteOne document will not have fileName, but will have inputeFieldSettings
        if (document.inputFieldSettings) {
            if (typeof document.inputFieldSettings === 'string') {
                document.inputFieldSettings = JSON.parse(document.inputFieldSettings);
            }

            // Check the inputFieldSettings for the third party fields
            const keys = Object.keys(document.inputFieldSettings);

            if (keys.length > 0) {
                const field = document.inputFieldSettings[keys[0]];

                if (field && field.thirdPartyName && field.thirdPartyType) {
                    return true;
                }
            }
        }
        return false;
    }

    public createRouteOneData(data: any): string {
        let dataString = '';
        let points = [];
        const strokeIndexes = [];

        for (const stroke of data) {
            strokeIndexes.push(points.length);
            points = points.concat(stroke.points);
        }

        dataString +=
            `${points.length}\n` + // total number of points
            `${data.length}\n`; // total number of strokes

        for (const point of points) {
            dataString += `${Math.round(point.x)} ${Math.round(point.y)}\n`; // Each points X and then Y
        }

        for (const [index, pointIndex] of strokeIndexes.entries()) {
            // Do not want to apply line break to last line in data
            dataString += `${pointIndex}${index === strokeIndexes.length - 1 ? '' : '\n'}`; // Each strokes starting point
        }

        return dataString;
    }

    public updateRouteOneCredentials(dealerId: number, dealId: number, firstName: string, lastName: string, vin: string, r1DealerId: string) {
        return this.apiService
            .postV2$('api/RouteOne/UpdateUser', {
                RouteOneDealerId: r1DealerId,
                dealerId,
                dealId,
                firstName,
                lastName,
                vin,
            })
            .pipe(
                map((response) => {
                    return response;
                })
            );
    }

    public getRouteOneUserInfo(dealerId: number) {
        return this.apiService.getV2$<any>(`api/RouteOne/GetRouteOneUserInfo/${dealerId}`).pipe(
            map((response) => {
                return response;
            })
        );
    }
}
